// Layer inventory — single source of truth for the map (US-875 · AC-4/5/6).
//
// Toggle control, the visibility effect and the legend all read from THIS module
// so id / render-type / symbology never drift apart (design.md §2.2). Two exports:
//   - LAYERS         : one entry per togglable Mapbox layer (drives LayerToggle +
//                      the visibility effect in LayersContext).
//   - LEGEND_SECTIONS: legend rows keyed by the layer ids that make them visible
//                      (drives LegendOverlay). A row may map to several layers
//                      (e.g. "Fiber Gap" → highest + lowest), mirroring the mockup.
//
// COLOR NOTE (design.md §1): for the 2026-07-02 demo the tiles come from David's
// existing Mapbox style, so legend colors below are David's CURRENT layer colors
// (legacy `COLORS`) — NOT the "Atlas Standard" palette in theme/tokens.js. Colors
// live as DATA here on purpose: EPIC-16 / US-904 (style migration) swaps values,
// not code. Atlas Standard targets for that swap are noted per row.

import { rasterRamps } from "../theme/tokens";

// Layer categories shown as groups in the Map Config panel, in order.
//
// NOTE (US-878 UX consolidation): the "Boundaries" group was removed from the
// toggle. Admin border lines (`admin-level1`/`admin-level2`) are no longer a
// standalone toggle — they follow the active selection level via the
// GeographicFocus block (see selectionConfig `boundaryLayers`). The layers stay
// in `LAYERS` below (the level coupling drives them); they just carry no
// user-facing toggle row.
export const CATEGORIES = [
  { key: "gaps", label: "Connectivity Gaps" },
  { key: "infrastructure", label: "Infrastructure" },
  { key: "anchor", label: "Anchor Institutions" },
];

// Render type guards how a layer is drawn — gaps/coverage as TILES (fill layers
// of the style), anchors as POINTS (circle layers). Keeps the mockup's
// circle-render from leaking back onto the gap layers (AC-4 / AC-5).
export const RENDER_TILE = "tile";
export const RENDER_POINT = "point";

// — Anchor institution clusters (US-922 · R-1..R-4/R-7) ————————————————————————
// The anchor_points tileset ships nothing below z6 by data design (806k raw
// points aren't servable at country zoom). These pre-aggregated cluster tiers
// bridge the dead zone: province-level bubbles at low zoom, 0.2° bins at mid
// zoom, the real points from z6 as always. Everything here is DATA — MapView
// materializes it, the anchor toggles drive it via coupledIds (R-4), and
// every visual parameter (zoom cuts, thresholds, radii, offsets) tunes
// without touching map code.
export const ANCHOR_CLUSTER_SOURCE = {
  id: "apac-anchor-clusters",
  url: "mapbox://larivieratech-mapbox.apac_anchor_clusters",
};

// One entry per institution type. `classification` is the tileset's numeric
// type code (1=Schools, 2=Health, 3=Government — validated against the
// expected distribution); `color` matches the type's real-point/legend color
// (visual parity, R-2); `offset` is the "trefoil" translate (px) so bubbles
// of 2–3 concurrent types at the same spot read separately (R-4).
export const ANCHOR_CLUSTER_TYPES = [
  { key: "schools", toggleId: "Schools", classification: 1, color: "#0F4B01", offset: [0, -10] },
  { key: "health", toggleId: "Health Facilities", classification: 2, color: "#FA2929", offset: [9, 6] },
  { key: "government", toggleId: "Government Buildings", classification: 3, color: "#0F63F5", offset: [-9, 6] },
];

// Tier = which source-layer serves which zoom range; real points take over at
// z6 (anchor_points minzoom). Anti-saturation (R-3): `minRenderCount` drops
// micro-bins entirely (57,898 bins with a median of 3 institutions rained
// micro-bubbles — nothing is lost: they stay counted in the admin1 bubbles
// and appear as real points at z6); `minLabelCount` keeps label rain down.
// `radiusStops` pair [sqrt(count), px] — `interpolate` clamps past the last
// stop, which is the cap absorbing the tail (China's country-residual bubble
// is 157k institutions: √≈396 → capped at the top radius).
export const ANCHOR_CLUSTER_TIERS = [
  {
    key: "admin1",
    sourceLayer: "admin1_clusters",
    minzoom: 0,
    maxzoom: 4.5,
    minRenderCount: 1,
    minLabelCount: 50,
    radiusStops: [[1, 4], [10, 8], [45, 14], [130, 22], [300, 30]],
  },
  {
    key: "bins",
    sourceLayer: "bin_clusters",
    minzoom: 4.5,
    maxzoom: 6,
    minRenderCount: 5,
    minLabelCount: 5,
    radiusStops: [[2.2, 4], [7, 8], [22, 13], [70, 18]],
  },
];

// Derived layer ids — one circle + one label per type×tier (12 total). The
// per-toggle map feeds the anchor entries' coupledIds below, so the acople
// is pure config: the existing companion mechanism does the rest.
export const anchorClusterLayerId = (type, tier, kind) =>
  `anchor-cluster-${tier.key}-${type.key}-${kind}`;
export const ANCHOR_CLUSTER_LAYER_IDS = ANCHOR_CLUSTER_TYPES.reduce((acc, type) => {
  acc[type.toggleId] = ANCHOR_CLUSTER_TIERS.flatMap((tier) => [
    anchorClusterLayerId(type, tier, "circle"),
    anchorClusterLayerId(type, tier, "label"),
  ]);
  return acc;
}, {});

// Togglable layers. `coupledIds` are the China-clipped companion layers that must
// flip together with their parent (legacy `coupledLayerMap`). `id`/`coupledIds`
// are Mapbox layer ids inside David's style.
export const LAYERS = [
  // — Connectivity gaps —
  // US-920 (R-1/R-3): the fixed/4G gap toggles drive the CONTINUOUS RASTER
  // layers (added at runtime from RASTER_LAYERS below). The legacy vector
  // layers stay in the style but are forced hidden (REPLACED_VECTOR_LAYERS)
  // — instant rollback by visibility, and reserved for future per-cell
  // interactivity. The China vector companions stay coupled: the raster is
  // veiled over China (cartographic doctrine), so China keeps its current
  // vector rendering.
  // DEFAULTS (US-920 · A-3): spec baseline — Highest ON, Lowest OFF, 4G ON —
  // restored after the ISSUE-451 republish. The tileset's bands were never
  // empty: MTS published them under CROSSED NAMES, so every pre-republish
  // band reading (empty outpop_fixed_high, "overlapping" surfaces) was wrong
  // data, not wrong config. Verified post-republish 2026-07-15:
  // outpop_fixed_high carries the gradual severity field.
  // A-2: `vectorId` is each gap toggle's CELLS-mode rendering — the gap style
  // switch (Continuous/Cells) decides which family the toggle paints; the
  // other family is forced hidden. State stays keyed by `id`.
  // A-9 (Lead UX): the two fiber densities form an EXCLUSIVE group — they
  // paint the same ≥300 ppl/km² class, so both-on has no real scenario and
  // one surface just occludes the other. Activating one auto-deactivates
  // its sibling (radio behavior, no dead clicks) in LayersContext.
  {
    id: "urban-gap-raster-high",
    vectorId: "urban-gap-high",
    label: "Fiber Gap (Highest)",
    description: "Dense areas ≥300 ppl/km² — fibre-suitable",
    category: "gaps",
    renderType: RENDER_TILE,
    coupledIds: ["urban-gap-china"],
    exclusiveGroup: "fiber",
    defaultVisible: true,
  },
  {
    id: "urban-gap-raster-low",
    vectorId: "urban-gap-low",
    label: "Fiber Gap (Lowest)",
    description: "Lower-density fixed-broadband gap",
    category: "gaps",
    renderType: RENDER_TILE,
    coupledIds: ["urban-gap-china"],
    exclusiveGroup: "fiber",
    defaultVisible: false,
  },
  {
    id: "gap-4g-raster",
    vectorId: "rural-gap",
    label: "4G Gap",
    description: "Rural 1–300 ppl/km² — FWA-suitable",
    category: "gaps",
    renderType: RENDER_TILE,
    coupledIds: ["rural-gap-china"],
    defaultVisible: true,
  },
  {
    // A-5: the satellite gap renders from the raster tileset too (no
    // dedicated band exists — see its RASTER_LAYERS window entry), so every
    // gap layer answers to the GeoTIFF pipeline. Vector cells = Cells mode.
    id: "satellite-gap-raster",
    vectorId: "extreme-rural-gap",
    label: "Satellite Gap",
    description: "Extreme-rural <1 ppl/km² — satellite",
    category: "gaps",
    renderType: RENDER_TILE,
    coupledIds: ["extreme-rural-gap-china"],
    defaultVisible: true,
  },
  // — Infrastructure —
  {
    id: "fiber-backbone",
    label: "Fiber Backbone",
    description: "National backbone routes & nodes",
    category: "infrastructure",
    renderType: RENDER_TILE,
    coupledIds: [],
    defaultVisible: false,
  },
  // NOTE (David C1, 2026-06-24): the "Public WiFi" layer
  // (`philippines_public_wifi_hotsp`) was a Philippines-only demo dataset with no
  // production data → removed from the app. It stays `visibility: none` in the
  // hosted style; the definitive cleanup (dropping the layer from David's style
  // `composite`) is tracked in QUESTIONS_ADB_DAVID.md. This voids decision D1.
  // — Anchor institutions (points) —
  // US-922 (R-4): each toggle also drives its 4 cluster layers (2 tiers ×
  // circle+label) through the ordinary coupled mechanism — the clusters
  // bridge z<6 where the real points don't exist.
  {
    id: "Health Facilities",
    label: "Health Facilities",
    description: "Hospitals & clinics",
    category: "anchor",
    renderType: RENDER_POINT,
    coupledIds: ANCHOR_CLUSTER_LAYER_IDS["Health Facilities"],
    defaultVisible: false,
  },
  {
    id: "Schools",
    label: "Schools",
    description: "Primary & secondary",
    category: "anchor",
    renderType: RENDER_POINT,
    coupledIds: ANCHOR_CLUSTER_LAYER_IDS["Schools"],
    defaultVisible: false,
  },
  {
    id: "Government Buildings",
    label: "Government Buildings",
    description: "Public administration",
    category: "anchor",
    renderType: RENDER_POINT,
    coupledIds: ANCHOR_CLUSTER_LAYER_IDS["Government Buildings"],
    defaultVisible: false,
  },
  // — Administrative boundaries (lines; no legend swatch) —
  {
    id: "admin-level1",
    label: "Provinces / States",
    description: "First-level admin boundaries",
    category: "boundaries",
    renderType: RENDER_TILE,
    coupledIds: [],
    defaultVisible: false,
  },
  {
    id: "admin-level2",
    label: "Districts",
    description: "Second-level admin boundaries",
    category: "boundaries",
    renderType: RENDER_TILE,
    coupledIds: [],
    defaultVisible: false,
  },
];

// Legend rows. `linkedLayers` = the toggle ids whose visibility reveals the row;
// a row shows when ANY linked layer is visible (LegendOverlay + AC-6). `shape`
// 'square' = tile swatch, 'circle' = point swatch. `atlas` records the EPIC-16
// target color so the future swap is a data change. Admin layers carry no row
// (parity with the legacy legend, which only listed coverage + anchors).
export const LEGEND_SECTIONS = [
  {
    title: "Map Layers",
    items: [
      {
        id: "fiber-gap",
        label: "Fiber Gap",
        color: "#FFD000", // Cells mode swatch — the style's vector fiber color
        shape: "square",
        linkedLayers: ["urban-gap-raster-high", "urban-gap-raster-low"],
        // US-920 (R-2): in Continuous mode this row renders the gradient
        // instead of the flat swatch (A-2: the gap-style switch decides).
        // R-10: extremes labeled to each class's real domain (percentile
        // stops), not the old raw-band [0, 20k]. R-11: China pixels come from
        // the household-based chn_* twins — the units note keeps the
        // "uncovered/km²" reading honest there (households, not people).
        gradient: {
          ramp: "fixedGap",
          minLabel: "low",
          maxLabel: "2k+ uncovered/km²",
          chinaNote: "China: households/km²",
        },
      },
      {
        id: "gap-4g",
        label: "4G Gap",
        color: "#1B4A83", // Cells mode swatch — the style's vector 4G color
        shape: "square",
        linkedLayers: ["gap-4g-raster"],
        gradient: {
          ramp: "mobileGap",
          minLabel: "low",
          maxLabel: "200+ uncovered/km²",
          chinaNote: "China: households/km²",
        },
      },
      {
        id: "gap-sat",
        label: "Satellite Gap",
        color: "#FF2424", // atlas: tokens.layerColors.satellite (#b8323a)
        shape: "square",
        // A-5: binary class — flat swatch in BOTH gap styles (no gradient).
        linkedLayers: ["satellite-gap-raster"],
      },
      {
        // Pseudo-row: "Served" has no toggle of its own — it is implied whenever
        // any gap layer is on (legacy special-case). `servedFor` drives that.
        id: "served",
        label: "Served",
        color: "rgb(214, 237, 207)", // atlas: tokens.layerColors.served (#4a8c3a)
        shape: "square",
        border: "1px solid rgba(0,0,0,0.2)",
        servedFor: [
          "urban-gap-raster-high",
          "urban-gap-raster-low",
          "gap-4g-raster",
          "satellite-gap-raster",
        ],
      },
      {
        id: "fiber-backbone",
        label: "Fiber Backbone",
        color: "#8b1887", // atlas: tokens.layerColors.backbone (#5a3d8a)
        shape: "square",
        linkedLayers: ["fiber-backbone"],
      },
    ],
  },
  {
    title: "Anchor Institutions",
    items: [
      {
        id: "health",
        label: "Health Facility",
        color: "#FA2929",
        shape: "circle",
        linkedLayers: ["Health Facilities"],
      },
      {
        id: "government",
        label: "Government Building",
        color: "#0F63F5",
        shape: "circle",
        linkedLayers: ["Government Buildings"],
      },
      {
        id: "school",
        label: "School",
        color: "#0F4B01",
        shape: "circle",
        linkedLayers: ["Schools"],
      },
    ],
  },
];

// Initial visibility map ({ [id]: boolean }) including coupled companions, built
// from `defaultVisible`. Consumed by LayersContext as the starting state.
export const initialVisibility = () => {
  const state = {};
  LAYERS.forEach((layer) => {
    state[layer.id] = layer.defaultVisible;
    layer.coupledIds.forEach((coupledId) => {
      // A companion shared by several parents (urban-gap-china belongs to
      // both fiber toggles) starts ON if ANY default-visible parent wants
      // it — plain assignment would let the last parent win (A-3).
      state[coupledId] = Boolean(state[coupledId]) || layer.defaultVisible;
    });
  });
  return state;
};

// Flatten layer id + its coupled companions — the full set to flip on a toggle.
export const layerWithCoupled = (layer) => [layer.id, ...layer.coupledIds];

// — Gap rendering styles (US-920 · A-2) ———————————————————————————————————————
// "raster" = continuous surfaces (default); "vector" = the legacy 1 km cells.
// One switch lets the stakeholder compare both visualizations; toggles and
// state stay keyed by the canonical layer id in either mode.
export const GAP_STYLE_RASTER = "raster";
// Raster layers stay layout-VISIBLE at all times so every tile load fetches
// their band chunks (a band whose layer is hidden at tile load never gets
// chunks; on GL 3.18 a later visibility flip never refetched them — the
// "toggled-on layer renders blank" bug. 3.25 does refetch on flip, but only
// after a network round-trip). Toggling drives raster-opacity between 0 and
// this value instead: version-proof and the toggle responds instantly.
export const RASTER_BASE_OPACITY = 0.85;
// A-8 (Lead UX): the "on" opacity is ZOOM-GRADUATED — full strength at
// regional zooms (the saturated overview look), easing down once a 1 km
// surface starts filling the screen so the basemap underneath (roads,
// towns) keeps the user oriented. This expression IS the on-state that
// LayersContext applies; 0 stays the off-state (band-chunk note above).
export const RASTER_OPACITY_BY_ZOOM = [
  "interpolate",
  ["linear"],
  ["zoom"],
  7,
  RASTER_BASE_OPACITY,
  10,
  0.55,
];
export const GAP_STYLE_VECTOR = "vector";
export const DEFAULT_GAP_STYLE = GAP_STYLE_RASTER;


// — Continuous raster gap layers (US-920 · R-1/R-3/R-8/R-10) ———————————————————
// The raster-array tileset published by MTS (24 named bands after the v4/v5
// republish of 2026-07-16, z0–8, 1 km grid). MapView materializes these
// entries at style load: one source, one raster layer per entry, inserted
// below the first symbol layer so borders/labels stay on top — and ONLY for
// bands present in the TileJSON the client actually loaded (cache-skew
// guard, R-10: a cached pre-republish TileJSON must degrade, not MRTError).
// The gap drivers are UNCOVERED PEOPLE PER CELL within each toggle's class
// — the same field the vector layers color by (outpop_adj), masked per
// class in v4.
export const RASTER_SOURCE = {
  id: "apac-grid-1km",
  url: "mapbox://larivieratech-mapbox.apac_grid_1km",
  rasterLayer: "grid_1km", // source-layer inside the tileset
};

// `stops` map data values (uncovered people/cell) onto the ramp steps,
// low→high. The domain top doubles as raster-color-range max; values above
// clamp to the dark end. Ramps live in theme/tokens (validated, see
// rasterRamps).
// R-10 (A-6): every entry paints its CLASS-MASKED band from the v4 republish
// — nodata = outside the toggle's labeled class (transparent), 0 = in class
// and served (transparent). The raw outpop_* bands painted every class at
// once (only 8.75% of lit Fiber-Highest pixels were in class), and the old
// [0, 20000] domain hid ~95% of in-class 4G values (raster-color builds a
// 256-texel LUT over raster-color-range; one texel was 78 units vs an
// in-class 4G p95 of 61). Stops below come from each class's real
// percentiles (v4_raster_plan.md).
// Array order = stacking order (each layer is inserted below the first
// symbol layer, after the previous one): 4G at the bottom, fiber above it —
// when both stories are toggled on, the fiber gap reads on top.
export const RASTER_LAYERS = [
  {
    // Dedicated binary satellite band (v4; replaces the A-5 population_density
    // approximation — Mongolia alone dropped ~601k false-positive cells).
    // Values are 100 (gap) / 0 (served); the window is deliberately WIDE
    // around 100: a tight window renders the value at partial alpha through
    // the 256-texel LUT. `window` entries render one flat color with nearest
    // resampling — a binary class, not a magnitude ramp.
    id: "satellite-gap-raster",
    band: "satellite_gap",
    window: { lo: 50, hi: 150, color: "#FF2424" },
  },
  {
    id: "gap-4g-raster",
    band: "gap_4g",
    ramp: "mobileGap",
    stops: [2, 10, 60, 200],
  },
  {
    id: "urban-gap-raster-low",
    band: "gap_fiber_low",
    ramp: "fixedGap",
    stops: [100, 500, 1000, 2000],
  },
  {
    id: "urban-gap-raster-high",
    band: "gap_fiber_high",
    ramp: "fixedGap",
    stops: [100, 500, 1000, 2000],
  },
];

// — China raster twins (US-920 · R-11) ————————————————————————————————————————
// China gaps come from the v5 chn_* bands — HOUSEHOLD metrics, the only 1 km
// China source that exists (the vendor never delivered CHN population or
// coverage), so China cannot share the APAC people-based bands. Each twin
// FOLLOWS the existing gap toggles instead of carrying its own: it paints
// when ANY parent toggle is on (China has one fiber band, so BOTH fiber
// toggles drive its twin — a single-parent map left the twin off with only
// Lowest on and the vector companion re-appeared) and only in Continuous
// mode; in Cells mode the vector *-china companions are the rendering.
// chn_gap_fiber is all-zero BY DESIGN (dense China is fully fixed-covered):
// it paints nothing and that is data, not a bug. chn-4g stops are household
// percentiles (p50 0.30 / p95 0.42 / max 0.44). MapView materializes these
// exactly like RASTER_LAYERS (same source, same band-availability guard).
export const CHN_RASTER_TWINS = [
  {
    id: "chn-sat-raster",
    band: "chn_gap_sat",
    window: { lo: 50, hi: 150, color: "#FF2424" },
    parents: ["satellite-gap-raster"],
  },
  {
    id: "chn-4g-raster",
    band: "chn_gap_4g",
    ramp: "mobileGap",
    stops: [0.05, 0.2, 0.3, 0.44],
    parents: ["gap-4g-raster"],
  },
  {
    id: "chn-fiber-raster",
    band: "chn_gap_fiber",
    ramp: "fixedGap",
    stops: [100, 500, 1000, 2000],
    parents: ["urban-gap-raster-high", "urban-gap-raster-low"],
  },
];

// The legacy vector gap layers (US-920 · R-3/A-2): forced hidden at style
// load because the DEFAULT gap style is the continuous raster — the
// Continuous/Cells switch re-reveals them per toggle state in Cells mode.
export const REPLACED_VECTOR_LAYERS = [
  "urban-gap-high",
  "urban-gap-low",
  "rural-gap",
  "extreme-rural-gap",
];

// Paint for one raster entry — pure and testable. Value 0 is transparent (a
// cell without gap paints nothing, R-8); linear resampling produces the
// continuous look (R-1). `window` entries (A-5) paint a flat color for
// values in (0, hi) instead of a ramp — binary class layers.
export const rasterPaint = (entry) => {
  if (entry.window) {
    const { lo = 0, hi, color } = entry.window;
    return {
      "raster-array-band": entry.band,
      "raster-resampling": "nearest",
      "raster-color": [
        "interpolate",
        ["linear"],
        ["raster-value"],
        0,
        "rgba(0,0,0,0)",
        // half a data step (tileset precision 0.01) around each edge: values
        // inside [lo, hi) paint solid, everything else stays transparent
        Math.max(lo - 0.005, 0.001),
        "rgba(0,0,0,0)",
        lo + 0.005,
        color,
        hi - 0.005,
        color,
        hi,
        "rgba(0,0,0,0)",
      ],
      // values ≥ hi clamp to the range top, which is transparent
      "raster-color-range": [0, hi],
      "raster-opacity": RASTER_OPACITY_BY_ZOOM,
      "raster-fade-duration": 300,
    };
  }
  const ramp = rasterRamps[entry.ramp];
  const rampStops = entry.stops.flatMap((value, i) => [value, ramp[i]]);
  return {
    "raster-array-band": entry.band,
    "raster-resampling": "linear",
    "raster-color": [
      "interpolate",
      ["linear"],
      ["raster-value"],
      0,
      "rgba(0,0,0,0)",
      ...rampStops,
    ],
    "raster-color-range": [0, entry.stops[entry.stops.length - 1]],
    "raster-opacity": RASTER_OPACITY_BY_ZOOM,
    // Cross-fade on tile load — a late tile eases in instead of popping (R-6).
    "raster-fade-duration": 300,
  };
};
